import { Fade, Slide } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import DataTable from "react-data-table-component";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTranslation } from "react-i18next";

import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import { GetColmn } from "../../../common/CustomColumn/customize_column";
import GetCapabilities from "../../../../utils/getCapabilities";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { changeTitle } from "../../../../state/slices/header";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess } from "../../../../utils/showMessage";
import {
  getFindings,
  acknowledgeFinding,
  markFindingFalsePositive,
  bulkFindingAction,
  bulkRecheckFindings,
} from "../../../../api/credentialScanning";
import SeverityBadge from "../components/SeverityBadge";
import FindingDetail from "./FindingDetail";
import Images from "../../../../utils/images";
import FirstPageIcon from "../../../../images/first_page.svg";
import LastPageIcon from "../../../../images/last_page.svg";
import NextPageIcon from "../../../../images/arrow_right.svg";
import PreviousPageIcon from "../../../../images/arrow_left.svg";
import { BsThreeDotsVertical } from "react-icons/bs";
import "../index.scss";

// ── Constants ──────────────────────────────────────────────────────────────

const pages_url = "dashboard/discovery/hardcoded-credentials/findings";

const STATUS_STYLES = {
  OPEN:           { bg: "#e3f2fd", color: "#1565c0" },
  ACKNOWLEDGED:   { bg: "#f3e5f5", color: "#6a1b9a" },
  REMEDIATED:     { bg: "#e8f5e9", color: "#2e7d32" },
  FALSE_POSITIVE: { bg: "#f5f5f5", color: "#757575" },
};

// English-keyed; serves as the stable identifier for column visibility.
// The customize-column popup currently displays these keys verbatim — a
// known gap; the table's header `name` field IS translated below.
const customizeColumnDict = {
  Severity:    true,
  Type:        true,
  Source:      true,
  Location:    true,
  Preview:     true,
  "PAM Match": true,
  Status:      true,
};

// ── Component ──────────────────────────────────────────────────────────────

function Findings() {
  const { t } = useTranslation();
  const dispatch       = useDispatch();
  const location       = useLocation();
  const { height }     = useWindowDimensions();

  // Filter/option arrays built from translation keys so labels follow the
  // active locale. Values stay verbatim — they're wire identifiers sent to
  // the backend and must not change per locale.
  const STATUS_FILTER_OPTIONS = useMemo(() => [
    { label: t("credentialScanning.statusOpen"),           value: "OPEN" },
    { label: t("credentialScanning.statusAcknowledged"),   value: "ACKNOWLEDGED" },
    { label: t("credentialScanning.statusRemediated"),     value: "REMEDIATED" },
    { label: t("credentialScanning.statusFalsePositive"), value: "FALSE_POSITIVE" },
  ], [t]);

  const SEVERITY_OPTIONS = useMemo(() => [
    { label: t("credentialScanning.severityCritical"), value: "CRITICAL" },
    { label: t("credentialScanning.severityHigh"),     value: "HIGH" },
    { label: t("credentialScanning.severityMedium"),   value: "MEDIUM" },
    { label: t("credentialScanning.severityLow"),      value: "LOW" },
  ], [t]);

  const SOURCE_OPTIONS = useMemo(() => [
    { label: t("credentialScanning.filterSourceSshAsset"), value: "ssh_asset" },
    { label: t("credentialScanning.filterSourceRdpSmb"),   value: "rdp_asset" },
    { label: t("credentialScanning.sourceDatabase"),       value: "database" },
    { label: t("credentialScanning.filterSourceGitRepo"),  value: "git_repo" },
  ], [t]);

  const TYPE_OPTIONS = useMemo(() => [
    { label: t("credentialScanning.typeDbPassword"),       value: "db_password" },
    { label: t("credentialScanning.typeApiKey"),           value: "api_key" },
    { label: t("credentialScanning.typeAwsKey"),           value: "aws_key" },
    { label: t("credentialScanning.typePrivateKey"),       value: "private_key" },
    { label: t("credentialScanning.typeOauthToken"),       value: "oauth_token" },
    { label: t("credentialScanning.typeGenericSecret"),    value: "generic_secret" },
    { label: t("credentialScanning.typeUsernamePassword"), value: "username_password" },
    { label: t("credentialScanning.typeSshKey"),           value: "ssh_key" },
  ], [t]);

  const ACTION_OPTIONS = useMemo(() => [
    { label: t("credentialScanning.actionAcknowledge"),        value: "acknowledge" },
    { label: t("credentialScanning.actionMarkFalsePositive"), value: "mark_false_positive" },
    { label: t("credentialScanning.actionRecheckSsh"),         value: "recheck" },
  ], [t]);

  const SOURCE_LABELS = useMemo(() => ({
    ssh_asset: t("credentialScanning.srcShortSshAsset"),
    rdp_asset: t("credentialScanning.srcShortRdpSmb"),
    database:  t("credentialScanning.sourceDatabase"),
    git_repo:  t("credentialScanning.srcShortGitRepo"),
  }), [t]);

  const datatable_height = height - 220 + "px";
  const my_capabilities  = GetCapabilities();
  const isCapable        = my_capabilities[0];

  // Table state
  const [findings,     setFindings]     = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [rowsPerPage,  setRowsPerPage]  = useState(
    Number(localStorage.getItem("rowsPerPage_Findings") || 25)
  );
  const [pending,      setPending]      = useState(true);
  const [initialLoad,  setInitialLoad]  = useState(true);
  const [slideChecked, setSlideChecked] = useState(false);

  // Selection / bulk action
  const [selectedRows,  setSelectedRows]  = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [action,        setAction]        = useState("");

  // Filters
  const [filterBy,             setFilterBy]             = useState("OPEN");
  const [search,               setSearch]               = useState("");
  const [appliedSearch,        setAppliedSearch]        = useState("");
  const [filterSeverity,       setFilterSeverity]       = useState("");
  const [filterSource,         setFilterSource]         = useState("");
  const [filterCredentialType, setFilterCredentialType] = useState(
    () => location.state?.credentialType || ""
  );
  // Scan-scoped + file-scoped filters set by the 'Files Scanned' tab when
  // a user clicks a file row to drill into its findings. Reading from
  // location.state keeps the URL pure (no query-string parsing) and matches
  // the existing credentialType pattern above.
  const filterScanId   = location.state?.scanId   || "";
  const filterFilePath = location.state?.filePath || "";

  // Column visibility
  const [checkedStateDict, setCheckedStateDict] = useState(customizeColumnDict);

  // Detail drawer
  const [detailFinding, setDetailFinding] = useState(null);

  useEffect(() => {
    GetColmn(setCheckedStateDict, pages_url, customizeColumnDict);
    dispatch(changeTitle("Discovery"));
    setSlideChecked(true);
    document.title = t("credentialScanning.headingFindings");
  }, [dispatch, t]);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadFindings = useCallback(async () => {
    setPending(true);
    const { data, error } = await getFindings({
      status:          filterBy             || undefined,
      severity:        filterSeverity       || undefined,
      source_type:     filterSource         || undefined,
      credential_type: filterCredentialType || undefined,
      search:          appliedSearch        || undefined,
      scan_id:         filterScanId         || undefined,
      file_path:       filterFilePath       || undefined,
      page,
      page_size: rowsPerPage,
    });
    if (data) {
      setFindings(data.findings || []);
      setTotal(data.total || 0);
    }
    if (error) showError(t("credentialScanning.toastFailedToLoadFindings"));
    setPending(false);
    setInitialLoad(false);
  }, [page, rowsPerPage, filterBy, filterSeverity, filterSource, filterCredentialType, appliedSearch, filterScanId, filterFilePath]);

  useEffect(() => { loadFindings(); }, [loadFindings]);

  // The columns array below is memoized on `[checkedStateDict, isCapable]`
  // and its row-level onClicks close over `loadFindings`. `loadFindings`
  // is a useCallback that recreates whenever `filterBy` / `page` / etc.
  // change — but the memoized columns hold a STALE reference to the
  // original one. Without this ref, clicking 'Recheck' on a row while
  // the filter shows REMEDIATED would re-fetch with the OLD filterBy=OPEN
  // and snap the table back to the open list. Routing every per-row
  // refetch through `loadFindingsRef.current()` always picks up the
  // newest closure.
  const loadFindingsRef = useRef(loadFindings);
  useEffect(() => { loadFindingsRef.current = loadFindings; }, [loadFindings]);
  const refreshFindings = useCallback(() => loadFindingsRef.current(), []);

  // Only fires on form submit (Enter key / search button) — no per-keystroke API calls
  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    setAppliedSearch(search);
  };

  // ── Row-level actions ─────────────────────────────────────────────────────

  const handleAcknowledge = async (id) => {
    await acknowledgeFinding(id);
    refreshFindings();
  };

  const handleFalsePositive = async (id) => {
    await markFindingFalsePositive(id);
    refreshFindings();
  };

  // ── Bulk action (called by SubHeaderComponent's Apply button) ─────────────

  const handleAction = async () => {
    const ids = selectedRows.map((r) => r.id);
    if (!ids.length) return;

    if (action === "recheck") {
      const { data, error } = await bulkRecheckFindings(ids);
      if (error) showError(error || t("credentialScanning.toastBulkRecheckFailed"));
      else       showSuccess(data.message);
    } else {
      const { error } = await bulkFindingAction(ids, action);
      if (error) showError(t("credentialScanning.toastBulkActionFailed"));
      else       showSuccess(t("credentialScanning.toastBulkActionApplied", { count: ids.length }));
    }
    setToggleCleared((prev) => !prev);
    setSelectedRows([]);
    refreshFindings();
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      {
        name: t("credentialScanning.colHdrSeverity"),
        selector: (row) => row.severity,
        sortable: true,
        minWidth: "110px",
        omit: checkedStateDict["Severity"] === false,
        cell: (row) => <SeverityBadge severity={row.severity} />,
      },
      {
        name: t("credentialScanning.colHdrType"),
        selector: (row) => row.credential_type,
        sortable: true,
        minWidth: "130px",
        omit: checkedStateDict["Type"] === false,
        cell: (row) => (
          <span style={{ fontSize: 13 }}>
            {row.credential_type?.replace(/_/g, " ")}
          </span>
        ),
      },
      {
        name: t("credentialScanning.colHdrSource"),
        selector: (row) => row.source_type,
        minWidth: "110px",
        omit: checkedStateDict["Source"] === false,
        cell: (row) => (
          <span style={{ fontSize: 13 }}>
            {SOURCE_LABELS[row.source_type] || row.source_type}
          </span>
        ),
      },
      {
        name: t("credentialScanning.colHdrLocation"),
        selector: (row) => row.file_path,
        grow: 2,
        omit: checkedStateDict["Location"] === false,
        cell: (row) => (
          <div style={{ fontSize: 11, overflow: "hidden", width: "100%" }}>
            {(row.asset_name || row.repository_name) && (
              <div
                style={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.asset_name || row.repository_name}
              </div>
            )}
            {row.file_path && (
              <div
                className="cred-scan-location-path"
                title={row.file_path + (row.line_number ? `:${row.line_number}` : "")}
              >
                {"…" + row.file_path.slice(-38)}
                {row.line_number ? `:${row.line_number}` : ""}
              </div>
            )}
          </div>
        ),
      },
      {
        name: t("credentialScanning.colHdrPreview"),
        minWidth: "120px",
        omit: checkedStateDict["Preview"] === false,
        cell: (row) => (
          <span style={{ fontFamily: "monospace", fontSize: 12, color: "#e53935" }}>
            {row.secret_preview}
          </span>
        ),
      },
      {
        name: t("credentialScanning.colHdrPamMatch"),
        minWidth: "110px",
        omit: checkedStateDict["PAM Match"] === false,
        cell: (row) =>
          row.matched_pam_username ? (
            <span
              style={{
                fontSize: 11,
                background: "#fff3e0",
                color: "#e65100",
                padding: "2px 6px",
                borderRadius: 4,
              }}
            >
              {row.matched_pam_username}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: "#aaa" }}>—</span>
          ),
      },
      {
        name: t("credentialScanning.colHdrStatus"),
        selector: (row) => row.status,
        sortable: true,
        minWidth: "130px",
        omit: checkedStateDict["Status"] === false,
        cell: (row) => {
          const s = STATUS_STYLES[row.status] || {};
          const statusKey = {
            OPEN: "credentialScanning.statusOpen",
            ACKNOWLEDGED: "credentialScanning.statusAcknowledged",
            REMEDIATED: "credentialScanning.statusRemediated",
            FALSE_POSITIVE: "credentialScanning.statusFalsePositive",
          }[row.status];
          return (
            <span
              style={{
                background: s.bg,
                color: s.color,
                padding: "3px 10px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {statusKey ? t(statusKey) : row.status?.replace(/_/g, " ")}
            </span>
          );
        },
      },
      {
        name: t("credentialScanning.colHdrAction"),
        center: true,
        minWidth: "100px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        cell: (row) => (
          <Nav>
            <NavDropdown
              id={`finding-action-${row.id}`}
              title={<BsThreeDotsVertical className="text-black" />}
              menuVariant="primary"
            >
              <NavDropdown.Item onClick={() => setDetailFinding(row)}>
                {t("credentialScanning.menuOpenDetails")}
              </NavDropdown.Item>
              {row.status === "OPEN" && isCapable?.credential_scanning?.manage_findings && (
                <>
                  <NavDropdown.Item onClick={() => handleAcknowledge(row.id)}>
                    {t("credentialScanning.menuAcknowledge")}
                  </NavDropdown.Item>
                  <NavDropdown.Item onClick={() => handleFalsePositive(row.id)}>
                    {t("credentialScanning.menuMarkFalsePositive")}
                  </NavDropdown.Item>
                </>
              )}
              {isCapable?.credential_scanning?.recheck && (
                <NavDropdown.Item
                  onClick={() =>
                    bulkRecheckFindings([row.id]).then(({ data, error }) => {
                      if (error) showError(error);
                      else { showSuccess(data.message); refreshFindings(); }
                    })
                  }
                >
                  {t("credentialScanning.menuRecheck")}
                </NavDropdown.Item>
              )}
            </NavDropdown>
          </Nav>
        ),
      },
    ],
    [checkedStateDict, isCapable, t, SOURCE_LABELS] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {initialLoad ? (
        <DataTableSkeleton rowCount={7} columnCount={8} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div className="main_content_container p-3 mx-auto w-100">
              <div
                className="position-relative bg-white"
                style={{ height: datatable_height }}
              >
                <DataTable
                  title={
                    <div>
                      <div className="py-2 pt-2">
                        <BreadcrumbHeading
                          dataTableHeadingText={[t("credentialScanning.breadcrumbBackTo"), t("credentialScanning.breadcrumbFindings")]}
                          dataTablePageLink={[
                            "/dashboard/discovery/hardcoded-credentials",
                            "/dashboard/discovery/hardcoded-credentials/findings",
                          ]}
                          pageNumber={4}
                        />
                      </div>
                      <div className="mb-3">
                        <ListHeading
                          dataTableHeadingText={t("credentialScanning.headingFindings")}
                          dataTableHeadingIcon={Images.NetworkAnalyzer}
                        />
                      </div>
                    </div>
                  }
                  columns={columns}
                  data={findings}
                  className="rdt_container"
                  noDataComponent={t("credentialScanning.noFindingsFound")}
                  fixedHeader
                  pagination
                  paginationServer
                  paginationTotalRows={total}
                  paginationPerPage={rowsPerPage}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  onChangePage={(p) => setPage(p)}
                  onChangeRowsPerPage={(n) => {
                    localStorage.setItem("rowsPerPage_Findings", n);
                    setRowsPerPage(n);
                    setPage(1);
                  }}
                  selectableRows
                  selectableRowsHighlight
                  highlightOnHover
                  onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                  clearSelectedRows={toggleCleared}
                  onRowClicked={(row) => setDetailFinding(row)}
                  pointerOnHover
                  actions={
                    <div className="d-flex align-items-center gap-2 py-1">
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 135 }}
                        value={filterSeverity}
                        onChange={(e) => { setFilterSeverity(e.target.value); setPage(1); }}
                      >
                        <option value="">{t("credentialScanning.allSeverities")}</option>
                        {SEVERITY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>

                      <select
                        className="form-select form-select-sm"
                        style={{ width: 125 }}
                        value={filterSource}
                        onChange={(e) => { setFilterSource(e.target.value); setPage(1); }}
                      >
                        <option value="">{t("credentialScanning.allSources")}</option>
                        {SOURCE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>

                      <select
                        className="form-select form-select-sm"
                        style={{ width: 140 }}
                        value={filterCredentialType}
                        onChange={(e) => { setFilterCredentialType(e.target.value); setPage(1); }}
                      >
                        <option value="">{t("credentialScanning.allTypes")}</option>
                        {TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  }
                  subHeader
                  subHeaderComponent={
                    <SubHeaderComponent
                      filterBy={filterBy}
                      setFilterBy={(v) => { setFilterBy(v); setPage(1); }}
                      filterOptions={STATUS_FILTER_OPTIONS}
                      action={action}
                      setAction={setAction}
                      handleAction={handleAction}
                      actionOptions={ACTION_OPTIONS}
                      search={search}
                      setSearch={setSearch}
                      handleSearch={handleSearch}
                      selectedRows={selectedRows}
                      placeholder={t("credentialScanning.searchPlaceholderFindings")}
                      pages_url={pages_url}
                      checkedStateDict={checkedStateDict}
                      setCheckedStateDict={setCheckedStateDict}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={(n) => {
                        localStorage.setItem("rowsPerPage_Findings", n);
                        setRowsPerPage(n);
                      }}
                      onRefresh={() => { setPending(true); loadFindings(); }}
                    />
                  }
                  subHeaderAlign="center"
                  paginationIconFirstPage={
                    <LazyLoadImage src={FirstPageIcon} alt="first page" />
                  }
                  paginationIconLastPage={
                    <LazyLoadImage src={LastPageIcon} alt="last page" />
                  }
                  paginationIconNext={<LazyLoadImage src={NextPageIcon} alt="next" />}
                  paginationIconPrevious={
                    <LazyLoadImage src={PreviousPageIcon} alt="previous" />
                  }
                />
              </div>
            </div>
          </Fade>
        </Slide>
      )}

      {detailFinding && (
        <FindingDetail
          finding={detailFinding}
          onClose={() => setDetailFinding(null)}
          onAcknowledge={() => { handleAcknowledge(detailFinding.id); setDetailFinding(null); }}
          onFalsePositive={() => { handleFalsePositive(detailFinding.id); setDetailFinding(null); }}
          onRemediated={() => { setDetailFinding(null); loadFindings(); }}
        />
      )}
    </>
  );
}

export default Findings;
